import { canv } from "@/canv/global";
import { drawRoundedRect, getNumberProperty, getStringProperty, isOverRect } from "@/canv/utils";
import { ElementBase } from "@/elementBase";
import type { SearchTextObject } from "@/elementBase/types";
import { regenerateTextPartUID } from "@/elements/text/textPartFactory";
import { t } from "@/i18n";
import { widgetOption } from "@/widgetMenu/widgetMenu";
import { TextWidgetV2 } from "../text/textV2";
import type { WidgetStateMap as TextWidgetStateMap } from "../text/typesV2";
import { CardWidgetSlider } from "./slider";
import { CardWidgetStatusBar } from "./statusBar";
import type {
    AdvancedCardProgressDataItem,
    CardProgressData,
    CardProgressDataItem,
    CardWidgetStateMap,
} from "./types";

export class CardWidget extends ElementBase {
    constructor(startX: number, startY: number) {
        super();

        this.startX = startX;
        this.startY = startY;

        this.statusBar = new CardWidgetStatusBar(startX, startY);
        this.slider = new CardWidgetSlider(startX, startY, this.width);

        // Title
        this.textTitle = new TextWidgetV2();
        this.textTitle.menuTextSize(28);
        this.setupTextPart(this.textTitle, t("card.titlePlaceholder"), "titleFocus");

        // author and date
        this.textAuthor = new TextWidgetV2();
        this.textAuthor.menuTextSize(18);
        this.setupTextPart(this.textAuthor, t("card.authorPlaceholder"), "authorFocus");
        this.textAuthor.setText(canv.getInitDataProp("userName"));
        this.textAuthor.menuTextColor("#9c9c9c");

        // description
        this.textDescription = new TextWidgetV2();
        this.textDescription.menuTextColor("#545454", true);
        this.textDescription.menuTextSize(25);
        this.setupTextPart(
            this.textDescription,
            t("card.descriptionPlaceholder"),
            "descriptionFocus",
        );

        this.setProgress(this.progressData.todo.defaultValue);

        this.nothingWhenInteraction = true;
        this.customResize = true;
        this.disableOutline = true;
        this.allowSideResize = true;

        this.updateDims();
    }

    setupTextPart(textPart: TextWidgetV2, greyText: string | undefined, autosaveReason: string) {
        textPart.setResizeMode("shape");
        textPart.setEditingAutosaveHandler(() => {
            canv.eventManager.openRecord();
            this.saveState(autosaveReason);
            canv.eventManager.closeRecord();
            this.fixState(autosaveReason);
        });

        textPart.setKeepMinWidth(false);

        if (greyText) {
            textPart.setGreyText(greyText);
        }
    }

    regenerateUIDS() {
        regenerateTextPartUID(this.textTitle, this.UID);
        regenerateTextPartUID(this.textAuthor, this.UID);
        regenerateTextPartUID(this.textDescription, this.UID);
    }

    updateDims() {
        const horizontalPadding = 23;
        const verticalPadding = 18;

        const getLineHeight = (textWidget: TextWidgetV2) => {
            return textWidget.lineHeight;
        };

        // положение statusBar
        this.statusBar.updateCoords(this.startX + horizontalPadding, this.startY + verticalPadding);

        const isTextTitleEditing = this.textTitle.getIsEditing();

        // положение title
        this.textTitle.hardWidth = this.width - 2 * horizontalPadding;
        this.textTitle.Fit();
        this.textTitle.startX = this.startX + horizontalPadding;
        this.textTitle.startY =
            this.statusBar.startY +
            this.statusBar.height +
            verticalPadding +
            getLineHeight(this.textTitle);

        if (!isTextTitleEditing) {
            this.textTitle.updateDims();
        }

        // положение slider
        this.slider.updateDims(
            this.startX + horizontalPadding,
            this.textTitle.startY +
                this.textTitle.height -
                getLineHeight(this.textTitle) +
                verticalPadding,
            this.width - 2 * horizontalPadding,
        );

        const isTextAuthorEditing = this.textAuthor.getIsEditing();

        // положение authorAndDate
        this.textAuthor.hardWidth = this.width - 2 * horizontalPadding;
        this.textAuthor.Fit();
        this.textAuthor.startX = this.startX + horizontalPadding;
        this.textAuthor.startY =
            this.slider.startY +
            this.slider.height +
            verticalPadding +
            getLineHeight(this.textAuthor);

        if (!isTextAuthorEditing) {
            this.textAuthor.updateDims();
        }

        const isTextDescriptionEditing = this.textDescription.getIsEditing();

        // положение description
        this.textDescription.hardWidth = this.width - 2 * horizontalPadding;
        this.textDescription.Fit();
        this.textDescription.startX = this.startX + horizontalPadding;
        this.textDescription.startY =
            this.textAuthor.startY +
            this.textAuthor.height -
            getLineHeight(this.textAuthor) +
            verticalPadding +
            getLineHeight(this.textDescription);

        if (!isTextDescriptionEditing) {
            this.textDescription.updateDims();
        }

        if (this.viewMode === "expanded") {
            this.height =
                this.textDescription.startY +
                this.textDescription.height -
                getLineHeight(this.textDescription) +
                verticalPadding -
                this.startY;
        } else {
            this.height =
                this.textAuthor.startY +
                this.textAuthor.height -
                getLineHeight(this.textAuthor) +
                verticalPadding -
                this.startY;
        }

        // проверяем минимальный width
        const minimumWidth =
            horizontalPadding +
            this.statusBar.width +
            horizontalPadding +
            this.getArrowProps().boxWidth +
            horizontalPadding; // тут последний horizontalPadding не очень корректен. При отрисовке используется другой.
        if (this.width < minimumWidth) {
            this.width = minimumWidth;
        }

        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    TryAction(type: string) {
        if (this.interactionType === "titleFocus") {
            this.textTitle.TryAction(type);
            this.updateDims();

            if (type === "keyboardDown") {
                this.tryShortcut();
            } else if (type === "mousedown" && !this.textTitle.isOver()) {
                this.stopInteraction();
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                canv.toolBox.TryGetToolByName("cursorTool").TryAction("mousedown");
            }
        } else if (this.interactionType === "descriptionFocus") {
            this.textDescription.TryAction(type);
            this.updateDims();

            if (type === "keyboardDown") {
                this.tryShortcut();
            } else if (type === "mousedown" && !this.textDescription.isOver()) {
                this.stopInteraction();
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                canv.toolBox.TryGetToolByName("cursorTool").TryAction("mousedown");
            }
        }
        if (this.interactionType === "authorFocus") {
            this.textAuthor.TryAction(type);
            this.updateDims();

            if (type === "keyboardDown") {
                this.tryShortcut();
            } else if (type === "mousedown" && !this.textAuthor.isOver()) {
                this.stopInteraction();
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                canv.toolBox.TryGetToolByName("cursorTool").TryAction("mousedown");
            }
        } else if (this.interactionType === "slider") {
            if (type === "mousemove") {
                const newProgress = this.slider.calculateProgress();
                this.setProgress(newProgress);
            } else if (type === "mouseup") {
                this.stopInteraction();
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
            }
        }
    }

    isInteraction(context: string, force = false) {
        if (context === "focus" || (context === "" && force)) {
            if (force || this.isTitleFocus()) {
                this.fixState("titleFocus");
                this.interactionType = "titleFocus";
                this.textTitle.focusText(context === "focus");
                return { type: "interaction" };
            } else if (this.isDescriptionFocus()) {
                this.fixState("descriptionFocus");
                this.interactionType = "descriptionFocus";
                this.textDescription.focusText(context === "focus");
                return { type: "interaction" };
            } else if (this.isAuthorFocus()) {
                this.fixState("authorFocus");
                this.interactionType = "authorFocus";
                this.textAuthor.focusText(context === "focus");
                this.textAuthor.selection.selectAll();
                return { type: "interaction" };
            }
        } else if (context === "click") {
            if (this.isStatusAction()) {
                // делает действие внутри isStatusAction
                return { type: "click" };
            } else if (this.isSliderAction()) {
                this.fixState("progress");
                this.interactionType = "slider";
                return { type: "interaction" };
            } else if (this.isOverArrow()) {
                this.fixState("viewMode");
                if (this.viewMode === "expanded") {
                    this.viewMode = "default";
                } else {
                    this.viewMode = "expanded";
                }
                canv.eventManager.openRecord();
                this.saveState("viewMode");
                canv.eventManager.closeRecord();
                this.updateDims();
                return { type: "click" };
            }
        } else if (context === "insertText") {
            this.fixState("titleFocus");
            this.interactionType = "titleFocus";
            if (canv.keyboard.isEnter()) {
                this.fixState("titleFocus");
                this.interactionType = "titleFocus";
                this.textTitle.focusTextByEnter();
            } else if (!canv.keyboard.isEnter()) {
                this.fixState("titleFocus");
                this.interactionType = "titleFocus";
                this.textTitle.focusText();
                canv.textEditingController.insertText(canv.keyboard.command, true);
                this.TryAction("keyboardDown");
            }
            this.updateDims();
            return {
                type: "interaction",
            };
        } else if (context === "pastText") {
            this.fixState("titleFocus");
            this.interactionType = "titleFocus";
            this.textTitle.focusText();
            canv.textEditingController.pasteFromClipboard(true);

            return {
                type: "interaction",
            };
        }

        return { type: "none" };
    }

    stopInteraction() {
        if (this.interactionType === "titleFocus") {
            this.textTitle.unfocusText();
            const shouldSave = !this.textTitle.consumeHostInteractionSaveSkip();
            if (shouldSave) {
                canv.eventManager.openRecord();
                this.saveState("titleFocus");
                canv.eventManager.closeRecord();
            }
        } else if (this.interactionType === "descriptionFocus") {
            this.textDescription.unfocusText();
            const shouldSave = !this.textDescription.consumeHostInteractionSaveSkip();
            if (shouldSave) {
                canv.eventManager.openRecord();
                this.saveState("descriptionFocus");
                canv.eventManager.closeRecord();
            }
        } else if (this.interactionType === "authorFocus") {
            this.textAuthor.unfocusText();
            const shouldSave = !this.textAuthor.consumeHostInteractionSaveSkip();
            if (shouldSave) {
                canv.eventManager.openRecord();
                this.saveState("authorFocus");
                canv.eventManager.closeRecord();
            }
        } else if (this.interactionType === "slider") {
            canv.eventManager.openRecord();
            this.saveState("progress");
            canv.eventManager.closeRecord();
        }

        this.interactionType = "";
    }

    tryShortcut() {
        if (canv.keyboard.isEscCommand()) {
            this.stopInteraction();
            canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
        }
    }

    draw() {
        drawRoundedRect(this.startX, this.startY, this.width, this.height, 15);
        canv.ctx.fillStyle = "#FFFFFF";
        canv.ctx.strokeStyle = this.getProgressData()?.minorColor;
        canv.ctx.lineWidth = 8;
        canv.ctx.stroke();
        canv.ctx.fill();

        canv.ctx.shadowBlur = 0;

        // statusBar
        this.statusBar.draw();
        // arrow
        this.drawArrow();
        // title
        this.textTitle.draw();

        // slider -------------------
        const isOnlySelected =
            canv.canvasController.selected.length === 1 &&
            canv.canvasController.isInSelected(this.parent) &&
            !canv.toolBox.TryGetToolByName("cursorTool").isMode("fastDragging");
        const drawCatcher =
            this.slider.isOverSliderCatcher() ||
            this.interactionType === "slider" ||
            isOnlySelected;
        const selectCatcher = this.interactionType === "slider";
        this.slider.draw(drawCatcher, selectCatcher);

        this.textAuthor.draw();

        if (this.viewMode === "expanded") {
            this.textDescription.draw();
        }

        this.drawOutline();
    }

    drawArrow() {
        const boxStartX = this.getArrowProps().boxStartX;
        const boxStartY = this.getArrowProps().boxStartY;
        const boxWidth = this.getArrowProps().boxWidth;
        const boxHeight = this.getArrowProps().boxHeight;

        const arrowWidth = this.getArrowProps().arrowWidth;
        const arrowHeight = this.getArrowProps().arrowHeight;

        if (this.viewMode === "default" && !this.textDescription.isOnlyEmptyLine()) {
            drawRoundedRect(boxStartX, boxStartY, boxWidth, boxHeight, boxWidth * 10);
            canv.ctx.fillStyle = "#BCE3FF";

            if (this.isOverArrow()) {
                canv.ctx.fillStyle = "#A5DAFF";
            }
            canv.ctx.fill();
        } else if (this.isOverArrow()) {
            drawRoundedRect(boxStartX, boxStartY, boxWidth, boxHeight, boxWidth * 10);
            canv.ctx.fillStyle = "#F9F9F9";
            canv.ctx.fill();
        }

        if (this.viewMode === "default") {
            canv.ctx.beginPath();
            canv.ctx.moveTo(
                boxStartX + boxWidth / 2 - arrowWidth / 2,
                boxStartY + boxHeight / 2 - arrowHeight / 2,
            );
            canv.ctx.lineTo(boxStartX + boxWidth / 2, boxStartY + boxHeight / 2 + arrowHeight / 2);
            canv.ctx.lineTo(
                boxStartX + boxWidth / 2 + arrowWidth / 2,
                boxStartY + boxHeight / 2 - arrowHeight / 2,
            );
        } else {
            canv.ctx.beginPath();
            canv.ctx.moveTo(
                boxStartX + boxWidth / 2 - arrowWidth / 2,
                boxStartY + boxHeight / 2 + arrowHeight / 2,
            );
            canv.ctx.lineTo(boxStartX + boxWidth / 2, boxStartY + boxHeight / 2 - arrowHeight / 2);
            canv.ctx.lineTo(
                boxStartX + boxWidth / 2 + arrowWidth / 2,
                boxStartY + boxHeight / 2 + arrowHeight / 2,
            );
        }

        canv.ctx.lineCap = "round";
        canv.ctx.lineWidth = 2;
        canv.ctx.strokeStyle = "#000000";
        canv.ctx.stroke();

        canv.ctx.closePath();
    }

    drawOutline() {
        // рисуем outline сами, чтобы сделать его закругленным

        const shouldDraw =
            canv.canvasController.isInSelectedDeep(this.parent) &&
            !canv.toolBox.TryGetToolByName("cursorTool").isMode("fastDragging");

        if (!shouldDraw) return;

        drawRoundedRect(this.startX, this.startY, this.width, this.height, 15);

        if (this.interactionType === "") {
            canv.ctx.strokeStyle = "#0B99FF";
        } else {
            canv.ctx.strokeStyle = "#BCE3FF";
        }
        canv.ctx.lineWidth = canv.actionManager.getZoomConstant(2.5);
        canv.ctx.stroke();
    }

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;
        this.updateDims();
    }

    resize(x: number, y: number, catchPointer: string) {
        if (catchPointer === "sideRight" || catchPointer === "sideLeft") {
            this.width += x;
        }

        this.updateDims();
    }

    isStatusAction() {
        if (this.statusBar.isStatusClick()) {
            const data = this.getProgressData();

            let newStatus = "todo";

            if (data.name === "todo") {
                newStatus = "progress";
            } else if (data.name === "progress") {
                newStatus = "done";
            } else if (data.name === "done") {
                newStatus = "todo";
            }

            canv.eventManager.openRecord();
            this.fixState("progress");
            this.setProgress(this.progressData[newStatus].defaultValue);
            this.saveState("progress");
            canv.eventManager.closeRecord();
            return true;
        } else if (this.statusBar.isCheckboxClick()) {
            canv.eventManager.openRecord();
            this.fixState("progress");

            if (this.getProgressData().name === "done") {
                this.setProgress(this.progressData.progress.defaultValue);
            } else {
                this.setProgress(this.progressData.done.defaultValue);
            }

            this.saveState("progress");
            canv.eventManager.closeRecord();
            return true;
        }
    }

    isTitleFocus() {
        if (this.textTitle.isOver()) {
            return true;
        }
        return false;
    }

    isDescriptionFocus() {
        if (this.textDescription.isOver()) {
            return true;
        }
        return false;
    }

    isAuthorFocus() {
        if (this.textAuthor.isOver()) {
            return true;
        }
        return false;
    }

    isSliderAction() {
        return this.slider.isOverSliderCatcher();
    }

    isOverArrow() {
        const boxStartX = this.getArrowProps().boxStartX;
        const boxStartY = this.getArrowProps().boxStartY;
        const boxWidth = this.getArrowProps().boxWidth;
        const boxHeight = this.getArrowProps().boxHeight;

        if (isOverRect(boxStartX, boxStartY, boxWidth, boxHeight)) {
            return true;
        }

        return false;
    }

    getArrowProps() {
        const arrowWidth = 10;
        const arrowHeight = 7;

        const horizontalPadding = 30;
        const boxWidth = 33;
        const boxHeight = 33;

        const boxStartX = this.startX + this.width - horizontalPadding - boxWidth;
        const boxStartY = this.statusBar.startY + this.statusBar.height / 2 - boxHeight / 2;

        return {
            boxStartX: boxStartX,
            boxStartY: boxStartY,
            boxWidth: boxWidth,
            boxHeight: boxHeight,
            arrowWidth: arrowWidth,
            arrowHeight: arrowHeight,
        };
    }

    setProgress(progress: number) {
        this.progress = progress;

        const data = this.getProgressData();

        const progressData = {
            ...data,
            title: t(`card.status.${data.name}`),
        };

        this.statusBar.setProgressData(progressData, this.interactionType !== "slider");
        this.slider.setProgressData(progressData, this.interactionType !== "slider");
    }

    getProgressData(): AdvancedCardProgressDataItem {
        if (this.progress >= this.progressData.done.border) {
            return {
                ...(this.progressData.done as CardProgressDataItem),
                currentProgress: this.progress,
                title: "",
            };
        }

        if (this.progress >= this.progressData.progress.border) {
            return {
                ...(this.progressData.progress as CardProgressDataItem),
                currentProgress: this.progress,
                title: "",
            };
        }

        return {
            ...(this.progressData.todo as CardProgressDataItem),
            currentProgress: this.progress,
            title: "",
        };
    }

    clearText() {
        this.textTitle.clearText();
        this.textDescription.clearText();
        this.textAuthor.clearText();
        this.updateDims();
    }

    searchText(text: string) {
        const result: SearchTextObject[] = [];
        const widgets = [this.textTitle, this.textDescription, this.textAuthor];

        for (let i = 0; i < widgets.length; i++) {
            const textWidget = widgets[i].getText().toLowerCase();

            if (textWidget.includes(text)) {
                result.push({
                    status: true,
                    textWidget: widgets[i],
                    mode: "widget",
                });
            }
        }

        return result;
    }

    getWidgetOptions() {
        const widgetOptions: widgetOption[] = [];

        widgetOptions.push(
            new widgetOption("check", this.getProgressData().name === "done", this.UID),
        );

        if (this.interactionType === "descriptionFocus") {
            widgetOptions.push(
                new widgetOption("link", this.textDescription.styleSummary.link, this.UID),
            );
        } else if (this.interactionType === "authorFocus") {
            widgetOptions.push(
                new widgetOption("link", this.textAuthor.styleSummary.link, this.UID),
            );
        } else if (this.interactionType === "titleFocus") {
            widgetOptions.push(
                new widgetOption("link", this.textTitle.styleSummary.link, this.UID),
            );
        }

        return widgetOptions;
    }

    getHandlerForOption(name: string) {
        if (name === "check") {
            return this.menuCheck;
        } else if (name === "link") {
            return this.menuLink;
        }
    }

    menuLink(currentLink: string) {
        if (this.interactionType === "titleFocus") {
            this.textTitle.menuTextLink(currentLink);
        } else if (this.interactionType === "descriptionFocus") {
            this.textDescription.menuTextLink(currentLink);
        } else if (this.interactionType === "authorFocus") {
            this.textAuthor.menuTextLink(currentLink);
        } else {
            canv.toolBox.TryGetToolByName("cursorTool").setSelectedAndInteraction(this.parent);
            canv.textEditingController.selectAll();
            this.textTitle.menuTextLink(currentLink);
        }
    }

    menuCheck(check: boolean) {
        if (check) {
            this.setProgress(this.progressData.done.defaultValue);
        } else {
            this.setProgress(this.progressData.progress.defaultValue);
        }
    }

    getFixType(reason: string) {
        let fixType = "full";

        switch (reason) {
            case "move":
                fixType = "move";
                break;
            case "resize":
                fixType = "resize";
                break;
            case "wm_check":
            case "progress":
                fixType = "progress";
                break;
            case "viewMode":
                fixType = "viewMode";
                break;
        }

        return fixType;
    }

    getState<FixType extends keyof CardWidgetStateMap>(
        fixType: FixType,
    ): CardWidgetStateMap[FixType] {
        const state: CardWidgetStateMap = {
            move: {
                startX: this.startX,
                startY: this.startY,
            },
            resize: {
                startX: this.startX,
                startY: this.startY,
                width: this.width,
            },
            progress: {
                progress: this.progress,
            },
            viewMode: {
                viewMode: this.viewMode,
            },
            full: {
                UID: this.UID,
                parentUID: this.parentUID,
                entityType: "cardWidget",
                startX: this.startX,
                startY: this.startY,
                width: this.width,
                progress: this.progress,
                viewMode: this.viewMode,
                textTitle: this.textTitle.getState("full") as TextWidgetStateMap["full"],
                textDescription: this.textDescription.getState(
                    "full",
                ) as TextWidgetStateMap["full"],
                textAuthor: this.textAuthor.getState("full") as TextWidgetStateMap["full"],
            },
        };

        return state[fixType];
    }

    loadState(stateObj, fixType) {
        switch (fixType) {
            case "move":
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                break;
            case "resize":
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.width = getNumberProperty("width", stateObj);
                break;
            case "progress":
                this.setProgress(getNumberProperty("progress", stateObj));
                break;
            case "viewMode":
                this.viewMode = getStringProperty("viewMode", stateObj, "default");
                break;
            case "full":
                this.UID = getStringProperty("UID", stateObj);
                this.parentUID = getStringProperty("parentUID", stateObj);

                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.width = getNumberProperty("width", stateObj);

                this.setProgress(getNumberProperty("progress", stateObj));
                this.viewMode = getStringProperty("viewMode", stateObj, "default");

                this.textTitle.loadState(stateObj.textTitle, "full");
                this.textDescription.loadState(stateObj.textDescription, "full");
                this.textAuthor.loadState(stateObj.textAuthor, "full");

                this.setupTextPart(this.textTitle, undefined, "titleFocus");
                this.setupTextPart(this.textAuthor, undefined, "authorFocus");
                this.setupTextPart(this.textDescription, undefined, "descriptionFocus");

                break;
        }

        this.updateDims();
    }

    constructorName: "card" = "card";
    startX = 0;
    startY = 0;
    width = 450;
    height = 220;

    interactionType = "";

    statusBar: CardWidgetStatusBar;
    slider: CardWidgetSlider;
    textTitle: TextWidgetV2;
    textAuthor: TextWidgetV2;
    textDescription: TextWidgetV2;

    viewMode = "default";

    progress = 0;
    progressData: CardProgressData = {
        todo: {
            name: "todo",
            border: 0,
            defaultValue: 0.09,
            minorColor: "#ECECEC",
            majorColor: "#5B5B5B",
        },
        progress: {
            name: "progress",
            border: 0.1,
            defaultValue: 0.5,
            minorColor: "#F9E8C7",
            majorColor: "#DE9A19",
        },
        done: {
            name: "done",
            border: 0.8,
            defaultValue: 1,
            minorColor: "#D4EBC3",
            majorColor: "#55A718",
        },
    };
}
